import { cn } from '@/lib/cn.ts';
import type { ReservationStatus } from '@okn/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { CornerDownLeft, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from './auth.ts';
import { useDebounce } from './lib/useDebounce.ts';
import { NAV_ITEMS } from './nav.ts';
import { StatusBadge } from './ui/Badge.tsx';

type ResItem = {
  id: string;
  referenceCode: string;
  fullName: string;
  phone: string;
  seatCount: number;
  status: ReservationStatus;
};

type Command =
  | {
      kind: 'nav';
      id: string;
      label: string;
      hint?: string;
      icon: React.ReactNode;
      run: () => void;
    }
  | { kind: 'res'; id: string; res: ResItem; run: () => void };

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ResItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebounce(q.trim(), 220);

  useEffect(() => {
    if (open) {
      setQ('');
      setResults([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    if (!open || debounced.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminApi<{ items: ResItem[] }>(
      `/admin/reservations?q=${encodeURIComponent(debounced)}&pageSize=6`,
    )
      .then((d) => !cancelled && setResults(d.items))
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced, open]);

  const go = useCallback(
    (to: string) => {
      onClose();
      navigate(to);
    },
    [navigate, onClose],
  );

  const commands = useMemo<Command[]>(() => {
    const ql = q.trim().toLowerCase();
    const nav: Command[] = NAV_ITEMS.filter(
      (n) => !ql || n.label.toLowerCase().includes(ql) || n.hint?.toLowerCase().includes(ql),
    ).map((n) => ({
      kind: 'nav',
      id: n.to,
      label: n.label,
      hint: n.hint,
      icon: <n.icon className="h-4 w-4" strokeWidth={1.75} />,
      run: () => go(n.to),
    }));
    const res: Command[] = results.map((r) => ({
      kind: 'res',
      id: r.id,
      res: r,
      run: () => go(`/admin/rezervasyonlar/${r.id}`),
    }));
    return [...nav, ...res];
  }, [q, results, go]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, commands.length - 1)));
  }, [commands.length]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % Math.max(1, commands.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + commands.length) % Math.max(1, commands.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commands[active]?.run();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[950] flex items-start justify-center px-4 pt-[12vh]">
          <motion.button
            type="button"
            aria-label="Kapat"
            onClick={onClose}
            className="modal-backdrop absolute inset-0 cursor-default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />
          {/* biome-ignore lint/a11y/useSemanticElements: animated palette can't be a native <dialog> */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Komut paleti"
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -6 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            className="modal-card relative w-full max-w-xl overflow-hidden rounded-xl"
          >
            <div className="flex items-center gap-3 border-b border-border px-4">
              <Search className="h-4 w-4 shrink-0 text-text-faint" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ekran ara veya rezervasyon bul (ad, telefon, referans)…"
                className="w-full bg-transparent py-4 text-sm text-text outline-none placeholder:text-text-faint"
                // biome-ignore lint/a11y/noAutofocus: palette is a focus trap by design
                autoFocus
              />
              {loading && (
                <span className="text-[10px] uppercase tracking-wider text-text-faint">…</span>
              )}
            </div>

            <div className="max-h-[52vh] overflow-y-auto p-1.5">
              {commands.length === 0 && (
                <p className="px-3 py-8 text-center text-sm text-text-faint">
                  Sonuç yok. Farklı bir terim deneyin.
                </p>
              )}
              {commands.map((c, i) => (
                <button
                  type="button"
                  key={`${c.kind}-${c.id}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => c.run()}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
                    i === active ? 'bg-accent-quiet' : 'hover:bg-surface',
                  )}
                >
                  {c.kind === 'nav' ? (
                    <>
                      <span className="text-text-faint">{c.icon}</span>
                      <span className="flex-1 text-sm text-text">{c.label}</span>
                      {c.hint && <span className="text-[11px] text-text-faint">{c.hint}</span>}
                    </>
                  ) : (
                    <>
                      <span className="mono text-xs text-accent tnum">{c.res.referenceCode}</span>
                      <span className="flex-1 truncate text-sm text-text">{c.res.fullName}</span>
                      <span className="mono text-[11px] text-text-faint tnum">{c.res.phone}</span>
                      <StatusBadge status={c.res.status} />
                    </>
                  )}
                  {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-text-faint" />}
                </button>
              ))}
            </div>

            <footer className="flex items-center gap-4 border-t border-border px-4 py-2 text-[10px] uppercase tracking-wider text-text-faint">
              <span>↑↓ gez</span>
              <span>↵ aç</span>
              <span>esc kapat</span>
            </footer>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
